import { API_URL } from '../../env'
import { DateFormatType, format } from '../../utils/date'
import { logAndReject } from '../../utils/promise'
import { FunnelOrder } from '../models/funnelOrder'
import { getValueFromDestionation } from '../models/funnelOrder/destination'
import { getRequestName } from '../models/funnelOrder/roomType'
import { getThemeAPIName, Theme } from '../models/funnelOrder/theme'
import {
  getValueForIncomingTime,
  getValueForOutgoingTime,
} from '../models/funnelOrder/times'
import {
  getSiteCountryICC2,
  getSiteCurrencyISO,
  SiteCountry,
  SiteCurrency,
} from '../models/locale'

interface SharedData {
  nr_people: number
  nr_babies: number
  nr_days: number
  site_country: string
  site_currency: string

  room_type: string
}

export interface IPrice {
  date: string
  total: number
  pp: number
  bookings?: number
  max_bookings?: number
  discount_pp?: number
}

export interface IAirport {
  id: number
  name_with_airports: string
  stopover?: boolean
  stopover_outside_eu?: boolean
  hide_outside_eu?: boolean
}

export interface IPricesResult {
  prices: IPrice[]
  airport_bundles?: IAirport[]
}

function buildSharedData(
  funnelOrder: FunnelOrder,
  currency: SiteCurrency,
  country: SiteCountry,
): SharedData {
  return {
    nr_people: funnelOrder.numberOfAdults + funnelOrder.numberOfChildren,
    nr_babies: funnelOrder.numberOfBabies,
    nr_days: funnelOrder.days,
    site_country: getSiteCountryICC2(country),
    site_currency: getSiteCurrencyISO(currency),

    room_type: getRequestName(funnelOrder.roomType),
  }
}

export function buildFlyData(
  funnelOrder: FunnelOrder,
  currency: SiteCurrency,
  country: SiteCountry,
) {
  const airports = new Map(funnelOrder.airports).get(country) || []

  const data: any = {
    theme: getThemeAPIName(funnelOrder.theme),
    ...buildSharedData(funnelOrder, currency, country),
  }
  if (airports.length !== 0) {
    data.airport_bundle_ids = airports.map(airport => airport.id)
  }

  if (funnelOrder.theme === Theme.totm) {
    return data
  } else if (
    funnelOrder.theme === Theme.multiFly ||
    funnelOrder.theme === Theme.solo
  ) {
    data.nr_destinations = funnelOrder.locations

    data.to_flight_between = getValueForOutgoingTime(funnelOrder.outgoingTime)
    data.return_flight_between = getValueForIncomingTime(
      funnelOrder.incomingTime,
    )
  } else {
    data.direction = getValueFromDestionation(funnelOrder.destination)

    data.to_flight_between = getValueForOutgoingTime(funnelOrder.outgoingTime)
    data.return_flight_between = getValueForIncomingTime(
      funnelOrder.incomingTime,
    )
    data.exclude_cities_ids = funnelOrder.excludedCitiesIds.map(x => x.id)
    data.include_cities_ids = funnelOrder.includedCitiesIds.map(x => x.id)
    if (funnelOrder.theme === Theme.zonvoordeel) {
      data.discount_code = 'zonvoordeel'
    }
  }
  return data
}

export function buildDriveData(
  funnelOrder: FunnelOrder,
  currency: SiteCurrency,
  country: SiteCountry,
) {
  return {
    nr_destinations: funnelOrder.locations,
    theme: getThemeAPIName(funnelOrder.theme),

    ...buildSharedData(funnelOrder, currency, country),
  }
}

export function queryParams(data: {
  [name: string]: string | number | number[]
}) {
  const params = new URLSearchParams()
  for (const [name, value] of Object.entries(data)) {
    if (value instanceof Array) {
      for (const val of value) {
        if (typeof val === 'string' || typeof val === 'number') {
          params.append(name, val.toString())
        }
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      params.append(name, value.toString())
    }
  }
  return params
}

export function fetchPrices(
  funnelOrder: FunnelOrder,
  currency: SiteCurrency,
  country: SiteCountry,
  fromDate?: Date,
): Promise<IPricesResult> {
  const formattedFromDate = fromDate
    ? format(fromDate, DateFormatType.apidate)
    : undefined

  switch (funnelOrder.theme) {
    case Theme.drive:
    case Theme.multiDrive:
    case Theme.train:
    case Theme.foodLoversDrive:
      const driveData: any = {
        ...buildDriveData(funnelOrder, currency, country),
      }
      if (formattedFromDate) {
        driveData.from_date = formattedFromDate
      }
      const driveParams = queryParams(driveData)
      return fetch(`${API_URL}/bookings/prices/drive/?${driveParams}`)
        .then(response => response.json())
        .catch(logAndReject)
    case Theme.fly:
    case Theme.multiFly:
    default:
      const flyData: any = { ...buildFlyData(funnelOrder, currency, country) }
      if (formattedFromDate) {
        flyData.from_date = formattedFromDate
      }
      const flyParams = queryParams(flyData)
      return fetch(`${API_URL}/bookings/prices/fly/?${flyParams}`)
        .then(response => response.json())
        .catch(logAndReject)
  }
}
